import { useEffect, useState } from "react";

/**
 * useMediaQuery — subscribes to a CSS media query and returns a boolean
 * that updates as the viewport changes. SSR-safe.
 *
 * Example: useMediaQuery("(max-width: 767px)") → true on phones.
 */
export function useMediaQuery(query) {
  const get = () =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(query).matches
      : false;
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const m = window.matchMedia(query);
    const onChange = () => setMatches(m.matches);
    onChange();
    // `addEventListener` is the modern API; older Safari uses addListener.
    if (m.addEventListener) {
      m.addEventListener("change", onChange);
      return () => m.removeEventListener("change", onChange);
    }
    m.addListener(onChange);
    return () => m.removeListener(onChange);
  }, [query]);
  return matches;
}

// Convenience aliases that match the breakpoints we use in the game UI.
// Mobile = phone-sized portrait/landscape (<768px wide).
// Tablet = 768–1023px (allowed to play portrait).
// Desktop = ≥1024px.
export const useIsMobile = () => useMediaQuery("(max-width: 767px)");
export const useIsTabletOrBelow = () => useMediaQuery("(max-width: 1023px)");
export const useIsPortrait = () => useMediaQuery("(orientation: portrait)");
